import { useEffect, useMemo, useState } from "react";
import { useSharedGutter } from "@/lib/width-group/use-shared-gutter";
import { type SharedGutter, type WidthGroupRegistry, widestGutter } from "@/lib/width-group/width-group-registry";
import { layoutTextMeasurer, type TextMeasurer } from "@/utils/text";
import type { AxisSpec, StreamEntry } from "../../ChartSpec";
import type { PreparedSeries } from "../prepare-series";
import {
    type AxisScale,
    computeAxisExtents,
    computeAxisScale,
    getBarAxisIds,
    targetTickCountForHeight,
} from "./axis-layout";
import { PLOT_HEADROOM_PX } from "./plot-geometry";
import { computeGutterRequirement, derivePlotLayout, derivePlotVerticalInsets, type PlotLayout } from "./plot-layout";

type PanelPlotLayout = { layout: PlotLayout; axisScales: Map<string, AxisScale> };

type PanelPlotLayoutInput = {
    axes: AxisSpec[];
    streams: StreamEntry[];
    preparedSeries: PreparedSeries[];
    activeLeftAxisId: string | undefined;
    panelHeightPx: number;
    groupId: string;
    memberId: string;
    registry: WidthGroupRegistry;
    /** Off by default: each panel sizes its own gutter and never joins the width group. */
    alignLeftGutters?: boolean;
    alignRightGutters?: boolean;
    /** Where the panel's event band sits, if it has one. */
    bandPosition?: "above" | "below";
    /** Injectable for tests; defaults to the shared cached canvas measurer. */
    measure?: TextMeasurer;
};

/**
 * Bump when web fonts finish loading so gutters remeasure after layoutTextMeasurer clears its
 * fallback-glyph cache (`createTextMeasurer` in `@/utils/text`). Clearing the cache alone does not
 * re-run React memos — this revision is the invalidation token.
 *
 * Effect registers after the first measure’s fonts.ready listener, so the cache clears before this
 * revision flips.
 */
function useDocumentFontsReadyRevision(): number {
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        const fonts = typeof document === "undefined" ? undefined : document.fonts;
        if (!fonts?.ready) return undefined;

        let cancelled = false;
        fonts.ready.then(
            () => {
                if (!cancelled) setRevision((n) => n + 1);
            },
            () => {
                // Ignore — stale gutters beat crashing.
            },
        );
        return () => {
            cancelled = true;
        };
    }, []);

    return revision;
}

/**
 * Computes plot layout: tick scales → how much gutter this panel needs → shared stack gutter → layout for ECharts and chrome.
 *
 * Gutter need must come from this panel's data only, not the shared gutter (or panels would keep widening each other).
 *
 * Call echarts.setOption from useLayoutEffect in panel.tsx, not useEffect — otherwise gutters jump one frame when a sibling mounts.
 *
 * Don't mutate layout.gutters; sibling panels may share that object.
 *
 * Layout ignores panel width (gutter need is from label text + tick count). Widening the panel only
 * needs chart.resize() — not a new PlotLayout / setOption.
 */
export function usePanelPlotLayout({
    axes,
    streams,
    preparedSeries,
    activeLeftAxisId,
    panelHeightPx,
    groupId,
    memberId,
    registry,
    alignLeftGutters = false,
    alignRightGutters = false,
    bandPosition,
    measure = layoutTextMeasurer,
}: PanelPlotLayoutInput): PanelPlotLayout {
    const fontsReadyRevision = useDocumentFontsReadyRevision();

    const extents = useMemo(() => computeAxisExtents(preparedSeries, streams, axes), [preparedSeries, streams, axes]);

    const insets = derivePlotVerticalInsets(bandPosition);
    const availablePlotHeightPx = Math.max(panelHeightPx - insets.plotTopPx - insets.plotBottomInsetPx, 1);
    const targetCount = targetTickCountForHeight(availablePlotHeightPx);

    const barAxisIds = useMemo(() => getBarAxisIds(streams), [streams]);

    const axisScales = useMemo(
        () =>
            new Map<string, AxisScale>(
                axes.flatMap((axis) => {
                    const extent = extents.get(axis.id);
                    if (!extent) return [];
                    const scale = computeAxisScale({
                        extent,
                        plotHeightPx: availablePlotHeightPx,
                        headroomPx: PLOT_HEADROOM_PX,
                        targetCount,
                        zeroBaseline: barAxisIds.has(axis.id),
                    });
                    return [[axis.id, scale] as const];
                }),
            ),
        [axes, extents, availablePlotHeightPx, targetCount, barAxisIds],
    );

    const requirement = useMemo(
        () =>
            computeGutterRequirement({
                axes,
                activeLeftAxisId,
                scalesByAxis: axisScales,
                measure,
            }),
        // fontsReadyRevision forces remeasure after webfonts load (tick labels use Roboto).
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional cache-bust dep
        [axes, activeLeftAxisId, axisScales, measure, fontsReadyRevision],
    );

    const alignsAnySide = alignLeftGutters || alignRightGutters;
    const sharedGutter = useSharedGutter(registry, groupId, memberId, requirement, alignsAnySide);

    // An aligned side takes whichever is wider: the shared gutter or our own measured need. The
    // "or wider" half matters on first render, before this panel has registered and while the
    // shared gutter is still 0 — without it the labels clip off the edge.
    //
    // Keyed on the numbers so new data on the same ticks keeps the same layout object.
    const { left: ownLeft, right: ownRight } = widestGutter([requirement]);
    const effectiveSharedGutter = useMemo<SharedGutter>(() => {
        const left = alignLeftGutters ? Math.max(sharedGutter.left, ownLeft) : ownLeft;
        const right = alignRightGutters ? Math.max(sharedGutter.right, ownRight) : ownRight;
        // Same object back when nothing differs, so sibling panels still compare equal by identity.
        if (left === sharedGutter.left && right === sharedGutter.right) return sharedGutter;
        return { left, right };
    }, [sharedGutter, ownLeft, ownRight, alignLeftGutters, alignRightGutters]);

    const layout = useMemo(
        () => derivePlotLayout({ sharedGutter: effectiveSharedGutter, bandPosition }),
        [effectiveSharedGutter, bandPosition],
    );

    return useMemo(() => ({ layout, axisScales }), [layout, axisScales]);
}
